// Auth-aware shell.
//
// `App` owns exactly two concerns:
//   1) Probe the session (`useMe`) and render a boot splash until it
//      resolves the first time.
//   2) When the session resolves anonymous on a protected route, kick
//      the user to /login with a `next=` redirect so they land back
//      where they were after signing in.
//
// Route definitions and individual pages live in `./routes`.

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { InkwellSplash } from "@/components/InkwellSplash";
import { useMe } from "@/data/auth";
import { AppRoutes } from "./routes";

const PUBLIC_PATHS = ["/login"] as const;
const PUBLIC_PREFIXES = ["/share/", "/invite/"] as const;

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.includes(pathname as (typeof PUBLIC_PATHS)[number])) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function App() {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();

  // Anonymous = the probe answered 401 (`data === null`) or failed
  // outright (network / 5xx).
  const anonymous = me.data === null || (me.isError && !me.data);

  // When we know we're anonymous and we're on a protected page, redirect.
  useEffect(() => {
    if (!anonymous) return;
    if (isPublicPath(location.pathname)) return;
    navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, {
      replace: true,
    });
  }, [anonymous, location.pathname, location.search, navigate]);

  // First boot only: render the splash while the very first session
  // probe is in flight. Later refetches (a query reset by logout, a
  // retry after a network error) must never unmount the routes.
  const bootedRef = useRef(false);
  if (!me.isPending) bootedRef.current = true;
  if (me.isPending && !bootedRef.current) return <BootSplash />;

  return <AppRoutes />;
}

// index.html paints a static copy of the splash (`#boot-splash`) inside
// #root before any JS runs. This module is evaluated before the first
// React render, so we can still see it here; if it was on screen, the
// wordmark has already been "written" and the React splash must not
// replay the intro when it takes over.
const STATIC_SPLASH_SHOWN =
  typeof document !== "undefined" && document.getElementById("boot-splash") !== null;

function BootSplash() {
  return <InkwellSplash intro={!STATIC_SPLASH_SHOWN} />;
}
